/**
 * Canvas-based map marker thumbnails (rounded card + optional count badge).
 */

const DISPLAY_PX = 64;
const DPR = 2;
const CANVAS_PX = DISPLAY_PX * DPR;
const CORNER_R = 10 * DPR;
const BORDER_PX = 3 * DPR;

const iconCache = new Map<string, string>();

export type GalleryMapIconSpec = {
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
};

function cacheKey(imageUrl: string | null, clusterCount: number): string {
  return `${imageUrl ?? ""}|${clusterCount}`;
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("img_load"));
    img.src = url;
  });
}

/** Fixed light palette (no CSS theme); optional future: read computed --muted / --background. */
function drawPlaceholderThumb(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fillStyle = "#e8e8ea";
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = BORDER_PX;
  ctx.stroke();
}

function drawBadge(ctx: CanvasRenderingContext2D, count: number) {
  const label = count > 99 ? "99+" : String(count);
  const fontSize = label.length >= 3 ? 9 * DPR : 11 * DPR;
  ctx.font = `700 ${fontSize}px system-ui, -apple-system, sans-serif`;
  const padX = 5 * DPR;
  const tw = ctx.measureText(label).width;
  const pillW = tw + padX * 2;
  const pillH = 22 * DPR;
  const rx = CANVAS_PX - BORDER_PX - pillW;
  const ry = BORDER_PX;

  ctx.shadowColor = "rgba(0,0,0,0.22)";
  ctx.shadowBlur = 4 * DPR;
  ctx.shadowOffsetY = 1 * DPR;
  ctx.fillStyle = "#ffffff";
  roundRectPath(ctx, rx, ry, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.fillStyle = "#1a1a1a";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, rx + pillW / 2, ry + pillH / 2);
}

/**
 * Rounded thumbnail marker; optional badge when clusterCount > 1.
 */
export async function buildGalleryMapMarkerIcon(
  imageUrl: string | null,
  options?: { clusterCount?: number }
): Promise<GalleryMapIconSpec> {
  const clusterCount = options?.clusterCount ?? 0;
  const showBadge = clusterCount > 1;
  const key = cacheKey(imageUrl, showBadge ? clusterCount : 0);
  const cached = iconCache.get(key);
  if (cached) {
    return {
      url: cached,
      width: DISPLAY_PX,
      height: DISPLAY_PX,
      anchorX: DISPLAY_PX / 2,
      anchorY: DISPLAY_PX,
    };
  }

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_PX;
  canvas.height = CANVAS_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    const url = canvas.toDataURL("image/png");
    iconCache.set(key, url);
    return { url, width: DISPLAY_PX, height: DISPLAY_PX, anchorX: DISPLAY_PX / 2, anchorY: DISPLAY_PX };
  }

  const innerX = BORDER_PX;
  const innerY = BORDER_PX;
  const innerW = CANVAS_PX - BORDER_PX * 2;
  const innerH = CANVAS_PX - BORDER_PX * 2;
  const innerR = CORNER_R - BORDER_PX * 0.5;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.32)";
  ctx.shadowBlur = 8 * DPR;
  ctx.shadowOffsetY = 3 * DPR;
  roundRectPath(ctx, innerX, innerY, innerW, innerH, innerR);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();

  let drewImage = false;
  if (imageUrl) {
    try {
      const img = await loadImage(imageUrl);
      drewImage = true;
      ctx.save();
      roundRectPath(ctx, innerX, innerY, innerW, innerH, innerR);
      ctx.clip();
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      const scale = Math.max(innerW / iw, innerH / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = innerX + (innerW - dw) / 2;
      const dy = innerY + (innerH - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.restore();

      roundRectPath(ctx, innerX, innerY, innerW, innerH, innerR);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = BORDER_PX;
      ctx.stroke();
    } catch {
      drewImage = false;
    }
  }

  if (!drewImage) {
    drawPlaceholderThumb(ctx, innerX, innerY, innerW, innerH, innerR);
  }

  if (showBadge) {
    drawBadge(ctx, clusterCount);
  }

  const url = canvas.toDataURL("image/png");
  iconCache.set(key, url);
  return {
    url,
    width: DISPLAY_PX,
    height: DISPLAY_PX,
    anchorX: DISPLAY_PX / 2,
    anchorY: DISPLAY_PX,
  };
}
